/**
 * UserMenu — circular avatar in the topbar that toggles a dropdown.
 *
 *   ┌───────────────┐
 *   │  [A]  ──────  │   signed-in: avatar initial
 *   └───────────────┘
 *        │
 *        ▼
 *   ┌─────────────────────────┐
 *   │  adam@example.com       │   email row
 *   │  ─────────────────────  │
 *   │  [ Sign out ]           │
 *   └─────────────────────────┘
 *
 * Signed-out state swaps the dropdown contents for a "Sign in" button
 * that invokes the caller-supplied `onSignIn` handler — the app can
 * wire that to open the preset browser's existing sign-in form.
 *
 * The avatar initial is pulled from the first character of the user's
 * email (or `?` as a neutral fallback while loading). Clicking the
 * avatar toggles the menu; clicking outside the component closes it.
 */

import type { AuthManager, AuthState } from '@/auth/AuthManager';
import type { InstallController } from '@/pwa/registerSW';

export interface UserMenuOptions {
  readonly auth: AuthManager;
  /**
   * Called when the user clicks "Sign in" in the signed-out dropdown.
   * Typical wiring: open the preset browser panel where the sign-in
   * form lives. Receiving this as a callback keeps the UserMenu
   * component independent of the PresetBrowser implementation.
   */
  readonly onSignIn?: () => void;
  /**
   * Optional PWA install controller. When provided, the dropdown
   * shows an "Install app" button while the browser considers the
   * page install-eligible (Chromium fires `beforeinstallprompt`).
   * The button hides itself once installed or when the controller
   * reports install isn't available.
   */
  readonly install?: InstallController;
}

export class UserMenu {
  readonly element: HTMLElement;

  private readonly avatarBtn: HTMLButtonElement;
  private readonly initialEl: HTMLElement;
  private readonly menuEl: HTMLElement;
  private readonly menuEmailEl: HTMLElement;
  private readonly signOutBtn: HTMLButtonElement;
  private readonly signInBtn: HTMLButtonElement;
  private readonly menuDivider: HTMLElement;

  private readonly auth: AuthManager;
  private readonly onSignIn: () => void;
  private readonly unsubAuth: () => void;
  private readonly onDocClick: (e: MouseEvent) => void;
  private readonly onDocKey: (e: KeyboardEvent) => void;

  private open = false;

  constructor(opts: UserMenuOptions) {
    this.auth = opts.auth;
    this.onSignIn = opts.onSignIn ?? ((): void => {});

    this.element = document.createElement('div');
    this.element.className = 'user-menu';

    // ── Avatar button ───────────────────────────────────────────────
    this.avatarBtn = document.createElement('button');
    this.avatarBtn.type = 'button';
    this.avatarBtn.className = 'user-menu-avatar';
    this.avatarBtn.setAttribute('aria-haspopup', 'menu');
    this.avatarBtn.setAttribute('aria-expanded', 'false');
    this.avatarBtn.setAttribute('aria-label', 'Account');
    this.initialEl = document.createElement('span');
    this.initialEl.className = 'user-menu-initial';
    this.initialEl.textContent = '?';
    this.avatarBtn.appendChild(this.initialEl);
    this.avatarBtn.addEventListener('click', () => this.toggle());
    this.element.appendChild(this.avatarBtn);

    // ── Dropdown menu ───────────────────────────────────────────────
    this.menuEl = document.createElement('div');
    this.menuEl.className = 'user-menu-dropdown';
    this.menuEl.setAttribute('role', 'menu');
    this.menuEl.hidden = true;

    this.menuEmailEl = document.createElement('div');
    this.menuEmailEl.className = 'user-menu-email mono';
    this.menuEmailEl.textContent = '';
    this.menuEl.appendChild(this.menuEmailEl);

    this.menuDivider = document.createElement('div');
    this.menuDivider.className = 'user-menu-divider';
    this.menuEl.appendChild(this.menuDivider);

    this.signOutBtn = document.createElement('button');
    this.signOutBtn.type = 'button';
    this.signOutBtn.className = 'btn btn-sm user-menu-action';
    this.signOutBtn.textContent = 'Sign out';
    this.signOutBtn.addEventListener('click', () => {
      this.setOpen(false);
      void this.auth.signOut();
    });
    this.menuEl.appendChild(this.signOutBtn);

    this.signInBtn = document.createElement('button');
    this.signInBtn.type = 'button';
    this.signInBtn.className = 'btn btn-sm user-menu-action';
    this.signInBtn.textContent = 'Sign in';
    this.signInBtn.addEventListener('click', () => {
      this.setOpen(false);
      this.onSignIn();
    });
    this.menuEl.appendChild(this.signInBtn);

    // ── Install app row. Shown only when the PWA layer says install
    // is currently available (Chromium fires `beforeinstallprompt`
    // for eligible pages). The button auto-hides on install + when
    // the deferred event has been consumed.
    if (opts.install) {
      const installDivider = document.createElement('div');
      installDivider.className = 'user-menu-divider';
      this.menuEl.appendChild(installDivider);

      const installBtn = document.createElement('button');
      installBtn.type = 'button';
      installBtn.className = 'btn btn-sm user-menu-action';
      installBtn.textContent = 'Install app';
      installBtn.addEventListener('click', () => {
        this.setOpen(false);
        void opts.install!.promptInstall();
      });
      this.menuEl.appendChild(installBtn);

      const refresh = (canInstall: boolean): void => {
        installBtn.hidden = !canInstall;
        installDivider.hidden = !canInstall;
      };
      refresh(opts.install.canInstall());
      // No need to track the unsubscribe explicitly — UserMenu lives
      // for the page lifetime, and dispose() clears its other listeners.
      opts.install.subscribe(refresh);
    }

    this.element.appendChild(this.menuEl);

    // ── Outside-click / Escape dismiss ──────────────────────────────
    // Attach to document so clicks anywhere else close the menu, but
    // mind that the opening click (on the avatar) is captured first
    // by its own handler — by the time document sees it, `this.open`
    // has already flipped on, and the contains() check keeps the
    // click alive.
    this.onDocClick = (e): void => {
      if (!this.open) return;
      const t = e.target;
      if (t instanceof Node && this.element.contains(t)) return;
      this.setOpen(false);
    };
    this.onDocKey = (e): void => {
      if (e.key === 'Escape' && this.open) {
        this.setOpen(false);
        this.avatarBtn.focus();
      }
    };
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onDocKey);

    this.unsubAuth = this.auth.subscribe((state) => this.renderAuthState(state));
    // `subscribe` replays current state synchronously so the initial
    // render has already happened by the time the constructor returns.
  }

  dispose(): void {
    this.unsubAuth();
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onDocKey);
  }

  private setOpen(open: boolean): void {
    this.open = open;
    this.menuEl.hidden = !open;
    this.avatarBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.element.classList.toggle('open', open);
  }

  private toggle(): void {
    this.setOpen(!this.open);
  }

  /**
   * Update the avatar + dropdown contents for the current auth state.
   * Signed-in: initial comes from the first letter of the email, email
   * row and sign-out button visible. Signed-out: neutral initial, only
   * the sign-in button visible. Loading: same as signed-out visually,
   * but the sign-in button is disabled so we don't race the session
   * restore.
   */
  private renderAuthState(state: AuthState): void {
    switch (state.kind) {
      case 'signed-in': {
        const email = state.user.email ?? '';
        this.initialEl.textContent = firstLetter(email);
        this.menuEmailEl.textContent = email || 'Signed in';
        this.menuEmailEl.hidden = false;
        this.menuDivider.hidden = false;
        this.signOutBtn.hidden = false;
        this.signInBtn.hidden = true;
        this.element.classList.remove('signed-out', 'loading');
        this.element.classList.add('signed-in');
        this.avatarBtn.setAttribute(
          'aria-label',
          email ? `Account — ${email}` : 'Account',
        );
        this.avatarBtn.title = email || 'Signed in';
        break;
      }
      case 'signed-out':
      case 'loading': {
        this.initialEl.textContent = '?';
        this.menuEmailEl.textContent = '';
        this.menuEmailEl.hidden = true;
        this.menuDivider.hidden = true;
        this.signOutBtn.hidden = true;
        this.signInBtn.hidden = false;
        this.signInBtn.disabled = state.kind === 'loading';
        this.element.classList.remove('signed-in');
        this.element.classList.toggle('loading', state.kind === 'loading');
        this.element.classList.toggle('signed-out', state.kind === 'signed-out');
        this.avatarBtn.setAttribute('aria-label', 'Account — signed out');
        this.avatarBtn.title = 'Signed out';
        break;
      }
    }
  }
}

/**
 * First grapheme of the email (uppercased). Falls back to `?` for an
 * empty string — keeps the avatar from collapsing visually during the
 * brief "loading" window before auth state resolves.
 */
function firstLetter(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return '?';
  // `Array.from` iterates by code point so astral characters (emoji
  // emails, wild world) don't chop into invalid surrogates.
  const first = Array.from(trimmed)[0];
  return (first ?? '?').toUpperCase();
}
